/**
 * Explicit time bounds for authentication work (FR-017, FR-032; Constitution IV "set an explicit
 * timeout on every outbound request"). `oidc-spa` exposes promises that can wait indefinitely
 * (discovery, code exchange, silent renewal, the login handoff), so every call site that waits on
 * one races it against a timer instead of trusting it to settle.
 */

/** Bound for boot-time discovery, code exchange and session restoration. */
export const OIDC_BOOT_TIMEOUT_MS = 20_000;

/** Bound for fetching the discovery document on its own. */
export const OIDC_DISCOVERY_TIMEOUT_MS = 10_000;

/** Bound for one silent `prompt=none` renewal attempt. */
export const SILENT_RENEWAL_TIMEOUT_MS = 15_000;

/** Bound for the SSO handoff to navigate away after `login()` is called. */
export const SSO_HANDOFF_TIMEOUT_MS = 10_000;

/** Bound for the one provider `end_session` attempt during cleanup. */
export const PROVIDER_LOGOUT_TIMEOUT_MS = 10_000;

/** Bound for releasing this browser's push device before sign-out clears its credentials. */
export const PUSH_DEVICE_RELEASE_TIMEOUT_MS = 3_000;

/** Raised when a bounded operation does not settle in time. */
export class AuthTimeoutError extends Error {
  constructor(operation: string, timeoutMs: number) {
    super(`${operation} did not settle within ${timeoutMs}ms`);
    this.name = 'AuthTimeoutError';
  }
}

/**
 * Resolves or rejects with `operation`'s own outcome, or rejects with {@link AuthTimeoutError} once
 * `timeoutMs` elapses first. The timer is always cleared, so a settled operation leaves nothing
 * pending.
 */
export async function withTimeout<T>(operation: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new AuthTimeoutError(label, timeoutMs)), timeoutMs);
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    clearTimeout(timeoutHandle);
  }
}
